<template>
    <div class="mt-[5px]">
        <ais-current-refinements class="mb-4 flex gap-2">
            <template #default="{ items }">
                <div
                    class="group cursor-pointer text-grind no-underline"
                    @click="actionClicked(getProductTypeFilters(items))"
                >
                    <div
                        class="relative mb-2 aspect-square rounded-lg border border-grind-200 ring-inset ring-grind transition group-hover:border-grind group-hover:ring-2"
                    >
                        <StoryblokImage
                            :src="
                                getCreateYourOwnImage(
                                    getProductTypeFilters(items),
                                )[0] || '/images/image-placeholder.svg'
                            "
                            :alt="
                                getCreateYourOwnImage(
                                    getProductTypeFilters(items),
                                )[1]
                                    ? getCreateYourOwnImage(
                                          getProductTypeFilters(items),
                                      )[1]
                                    : 'Product Image'
                            "
                            class="h-full w-full rounded-[10px] object-contain"
                            sizes="364px md:400px"
                        />
                    </div>
                    <div class="text-base">
                        <div class="font-bold">Create Your Own</div>
                        <div v-if="getProductTypeFilters(items).length === 1">
                            {{ theme.product_type }}
                        </div>
                        <div
                            v-if="getProductTypeFilters(items).length === 1"
                            class="font-bold"
                        >
                            Starting at {{ theme.minimum }} for ${{
                                theme.price_starts_at
                            }}
                        </div>
                    </div>
                </div>
            </template>
        </ais-current-refinements>
        <PickYourProductModal
            ref="pickYourProductModal"
            @sup-options="supOptions"
        />
    </div>
</template>

<script setup lang="ts">
import AlgoliaDesignLabTheme from '~/types/AlgoliaDesignLabTheme';
import { AisCurrentRefinements } from 'vue-instantsearch/vue3/es';
import { ref } from 'vue';
import PickYourProductModal from '~/components/page-building/design-lab-pick-your-product/components/PickYourProductModal.vue';
import {
    flatBottomBag,
    gussetBag,
    standUpPouch,
    tinCan4Inch,
    tinCan5Inch,
    tinCan7Inch,
} from '~/data/designLab/productIds';

const props = defineProps<{
    theme: AlgoliaDesignLabTheme | null;
    dummyTheme: AlgoliaDesignLabTheme;
}>();

const getCreateYourOwnImage = (productTypes): string[] => {
    const productTypeSearch =
        productTypes.length > 1 || productTypes.length === 0
            ? 'Default'
            : productTypes[0];
    const cardObj: AlgoliaDesignLabTheme =
        props.dummyTheme.create_your_own_card_images.find(
            (item: AlgoliaDesignLabTheme) =>
                item.product_type === productTypeSearch,
        );

    if (cardObj) {
        return [cardObj.image?.src, cardObj.image?.alt];
    }

    return ['', ''];
};

const pickYourProductModal = ref<InstanceType<
    typeof PickYourProductModal
> | null>(null);

const bagData = [
    {
        productId: gussetBag,
        name: 'Gusset Bag',
        subtitle: 'Holds 12-16oz | 25 for $64.75',
        src: '/images/design-lab/gusset.svg',
    },
    {
        productId: flatBottomBag,
        name: 'Flat Bottom Bag',
        subtitle: 'Holds 10-12oz | 100 for $426',
        src: '/images/design-lab/flat-bottom.svg',
    },
    {
        productId: standUpPouch,
        name: 'Stand Up Pouch',
        subtitle: 'Holds 10-12oz | 25 for $72',
        src: '/images/design-lab/stand-up-pouch.svg',
    },
];

const tinCanData = [
    {
        productId: tinCan4Inch,
        name: '4in Straight Tin Can',
        subtitle: 'Holds 6-8oz | 1 for $15',
        src: '/images/design-lab/4-tin-can.svg',
    },
    {
        productId: tinCan5Inch,
        name: '5.5in Beaded Tin Can',
        subtitle: 'Holds 8-12oz | 1 for $15',
        src: '/images/design-lab/5-tin-can.svg',
    },
    {
        productId: tinCan7Inch,
        name: '7in Straight Tin Can',
        subtitle: 'Holds 10-16oz | 1 for $15',
        src: '/images/design-lab/7-tin-can.svg',
    },
];

const actionClicked = (productFilters: string[]) => {
    const action = 'create';
    if (productFilters.length > 1 || productFilters.length === 0) {
        pickYourProductModal.value?.open(action, bagData.concat(tinCanData));
        return;
    }

    switch (productFilters[0]) {
        case 'Gusset Bag':
            navigateTo(
                '/roastar-design-lab#product=' +
                    gussetBag +
                    '&method=' +
                    action,
                {
                    external: true,
                    open: {
                        target: '_blank',
                    },
                },
            );
            break;
        case 'Flat Bottom Bag':
            navigateTo(
                '/roastar-design-lab#product=' +
                    flatBottomBag +
                    '&method=' +
                    action,
                {
                    external: true,
                    open: {
                        target: '_blank',
                    },
                },
            );
            break;
        case 'Stand Up Pouch':
            supOptions(action, '');
            break;
        case 'Tin Can':
            pickYourProductModal.value?.open(action, tinCanData);
            break;
    }
};

const emit = defineEmits(['supOptions']);

const supOptions = (method: string, theme: string) => {
    pickYourProductModal.value?.close();
    emit('supOptions', method, theme);
};
const getProductTypeFilters = (itemsRef) => {
    const attributeName = 'product_type';
    const currProductTypeRefinements = itemsRef.filter(
        (item) => attributeName === item.label,
    );
    if (currProductTypeRefinements.length <= 0) {
        return [];
    }

    return currProductTypeRefinements[0].refinements.map((refinement) => {
        return refinement.value;
    });
};
</script>
